import { useState } from "react"

import { Box, CheckCircle, ChevronUp, ClipboardList, Clock, Circle, ScanSearch } from "lucide-react"

import { FileBadgeLight } from "@components/badges/FileBadge"
import { ActionTooltipButton } from "@components/buttons/ActionTooltipButton"

import { Plan } from "@models/agents"
import { FileMessage } from "@models/messages"

import { Button } from "@ui/button"
import { Card, CardContent } from "@ui/card"

interface PlanPanelProps {
    files: FileMessage[]
    metrics?: Record<string, any>
    plan: Plan
    workstreamStatus: Map<string, "pending" | "in_progress" | "completed">
}
export function PlanPanel({ files, metrics, plan, workstreamStatus }: Readonly<PlanPanelProps>) {
    const [planOpen, setPlanOpen] = useState(true)
    const [filesOpen, setFilesOpen] = useState(true)
    const [metricsOpen, setMetricsOpen] = useState(true)

    const [showSearch, setShowSearch] = useState(false)
    const [filterValue, setFilterValue] = useState("")

    const filteredFiles = files.filter((file) => file.name.toLowerCase().includes(filterValue.toLowerCase()))

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.stopPropagation()
        setFilterValue(e.target.value)
    }

    const hasPlan = plan.plan && plan.plan.length > 0
    const hasFiles = files.length > 0
    const hasMetrics = metrics && Object.keys(metrics).length > 0

    return (
        <div className="h-full w-full overflow-hidden">
            <div className="h-[calc(100vh-55px)] p-3 overflow-y-auto">
                <div className="flex flex-col gap-4 h-full">
                    {hasPlan && (
                        <div className="p-2 bg-blue-50 rounded-md border border-blue-100">
                            <div className="text-xs font-medium text-blue-700 mb-1">Task Progress</div>
                            <div className="flex items-center gap-2">
                                {(() => {
                                    const planTasks = plan.plan || []
                                    const totalTasks = planTasks.length
                                    let completedTasks = 0

                                    if (totalTasks > 0) {
                                        planTasks.forEach((task) => {
                                            if (task && task.id) {
                                                const taskId = task.id.toString()
                                                const taskStatus = workstreamStatus.get(taskId)
                                                if (taskStatus === "completed") {
                                                    completedTasks++
                                                }
                                            }
                                        })
                                    }

                                    const progressPercentage = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0

                                    return (
                                        <>
                                            <div className="w-full bg-gray-200 rounded-full h-2">
                                                <div className="bg-blue-600 h-2 rounded-full" style={{ width: `${progressPercentage}%` }} />
                                            </div>
                                            <span className="text-xs text-gray-600">{totalTasks > 0 ? `${completedTasks}/${totalTasks}` : "0/0"}</span>
                                        </>
                                    )
                                })()}
                            </div>
                        </div>
                    )}
                    {hasPlan && (
                        <div className={`rounded-md border border-gray-200 overflow-hidden flex flex-col ${planOpen ? "flex-1" : ""}`}>
                            <div className={`p-2 rounded-t-md flex bg-gray-50 flex-row items-center justify-between flex-shrink-0 ${planOpen ? "border-b" : ""}`}>
                                <div className="flex items-center gap-2 font-medium text-xs">
                                    <Button onClick={() => setPlanOpen(!planOpen)} variant="ghost" size="xs" className="text-gray-900 hover:bg-gray-100">
                                        <ChevronUp className={`size-8  ${planOpen ? "rotate-180" : "rotate-0"}`} />
                                    </Button>
                                    Step-by-Step Plan
                                </div>
                                <Button size="sm" variant="ghost">
                                    <ClipboardList />
                                </Button>
                            </div>
                            <div className={`divide-y divide-gray-100 flex-1 overflow-y-auto ${planOpen ? "max-h-full" : "max-h-0"}`}>
                                {plan.plan.map((task, index) => {
                                    const taskId = task.id ? task.id.toString() : `task-${index}`
                                    const taskGoal = task.goal || `Task ${index + 1}`

                                    let status: "pending" | "in_progress" | "completed" | "skipped" = task.status || "pending"
                                    if (workstreamStatus.has(taskId)) {
                                        status = workstreamStatus.get(taskId)!
                                    }

                                    let StatusIcon = Circle
                                    let statusColor = "text-gray-400"
                                    let bgColor = ""

                                    if (status === "in_progress") {
                                        StatusIcon = Clock
                                        statusColor = "text-blue-500"
                                        bgColor = "bg-blue-50/50"
                                    } else if (status === "completed") {
                                        StatusIcon = CheckCircle
                                        statusColor = "text-green-500"
                                    }

                                    return (
                                        <div key={index} className={`flex flex-col gap-1 p-3 ${bgColor}`}>
                                            <div className="flex w-full justify-between items-center">
                                                <div className="flex items-center">
                                                    <div className={`mr-2 mt-0.5 flex-shrink-0 ${statusColor}`}>
                                                        <StatusIcon className="h-3.5 w-3.5" />
                                                    </div>
                                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">{taskId}</span>
                                                </div>
                                                <span className={`text-xs px-2 py-0.5 rounded-full ${status === "completed" ? "bg-green-100 text-green-600" : status === "in_progress" ? "bg-blue-100 text-blue-600" : "bg-gray-100 text-gray-500"}`}>{status === "completed" ? "Completed" : status === "in_progress" ? "In Progress" : "Pending"}</span>
                                            </div>
                                            <div className="w-full">
                                                <div className="text-sm font-medium text-gray-800">{taskGoal}</div>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    )}
                    {hasMetrics && (
                        <div className={`rounded-md border border-gray-200 overflow-hidden flex flex-col ${metricsOpen ? "flex-1" : ""}`}>
                            <div className={`p-2 rounded-t-md flex bg-gray-50 flex-row items-center justify-between flex-shrink-0 ${metricsOpen ? "border-b" : ""}`}>
                                <div className="flex items-center gap-2 font-medium text-xs">
                                    <Button onClick={() => setMetricsOpen(!metricsOpen)} variant="ghost" size="xs" className="text-gray-900 hover:bg-gray-100">
                                        <ChevronUp className={`size-8  ${metricsOpen ? "rotate-180" : "rotate-0"}`} />
                                    </Button>
                                    Metrics
                                </div>
                                <Button size="sm" variant="ghost">
                                    <Box />
                                </Button>
                            </div>
                            <div className={`divide-y divide-gray-100 flex-1 overflow-y-auto ${metricsOpen ? "max-h-full" : "max-h-0"}`}>
                                <div className="flex flex-col gap-4 p-4">
                                    <Card className="rounded-sm">
                                        <CardContent className="p-3 flex flex-row items-start justify-between gap-0.5">
                                            <div className="flex flex-1 flex-col items-start gap-2">
                                                <p className="text-xs text-muted-foreground">Content Analyzed</p>
                                                <p className="text-sm font-bold">{metrics.content_analyzed}</p>
                                            </div>
                                            <div className="flex flex-1 flex-col items-start gap-2">
                                                <p className="text-xs text-muted-foreground">Content Searched</p>
                                                <p className="text-sm font-bold">{metrics.content_searched}</p>
                                            </div>
                                        </CardContent>
                                    </Card>
                                    <Card className="rounded-sm">
                                        <CardContent className="p-3 flex flex-row items-start justify-between gap-0.5">
                                            <div className="flex flex-1 flex-col items-start gap-2">
                                                <p className="text-xs text-muted-foreground">Est. Reading Time</p>
                                                <p className="text-sm font-bold">{metrics.reading_time}</p>
                                            </div>
                                            <div className="flex flex-1 flex-col items-start gap-2">
                                                <p className="text-xs text-muted-foreground">Est. Writing Time</p>
                                                <p className="text-sm font-bold">{metrics.writing_time}</p>
                                            </div>
                                        </CardContent>
                                    </Card>
                                </div>
                            </div>
                        </div>
                    )}
                    {hasFiles && (
                        <div className={`rounded-md border border-gray-200 overflow-hidden flex flex-col ${filesOpen ? "flex-1" : ""}`}>
                            <div className={`p-2 rounded-t-md flex bg-gray-50 flex-row items-center justify-between flex-shrink-0 ${filesOpen ? "border-b" : ""}`}>
                                <div className="flex items-center gap-2 font-medium text-xs">
                                    <Button onClick={() => setFilesOpen(!filesOpen)} variant="ghost" size="xs" className="text-gray-900 hover:bg-gray-100">
                                        <ChevronUp className={`size-8  ${filesOpen ? "rotate-180" : "rotate-0"}`} />
                                    </Button>
                                    Associated Content ({filteredFiles.length})
                                </div>
                                <ActionTooltipButton size="sm" variant="ghost" icon={<ScanSearch />} tooltip="Search Content" onClick={() => setShowSearch(!showSearch)} className={`${showSearch ? "text-blue-500" : ""}`} />
                            </div>
                            <div className={`flex-1 overflow-y-auto ${filesOpen ? "max-h-full" : "max-h-0"}`}>
                                <div className={`overflow-hidden  ease-in-out bg-gray-50 ${!showSearch ? "h-0" : "p-2"}`}>
                                    <input value={filterValue} onChange={handleInputChange} type="text" placeholder="Search files..." className="w-full p-1 border border-gray-300 rounded-md text-xs" />
                                </div>
                                <div className="flex flex-col gap-2 p-2">
                                    {filteredFiles.map((file) => (
                                        <FileBadgeLight key={file.id} id={file.id} name={file.name} />
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
